import ChevronRight from '@assets/icons/chevron-right.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import {
  BORDER_RADIUS_SMALL,
  BORDER_RADIUS_TINY,
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { DisplayAdEventContent } from '@/types';
import { StyleSheet, Text, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  displayAdContent: DisplayAdEventContent;
  withinDetails?: boolean;
}

export const MOBILE_DISPLAY_AD_BANNER_ASPECT_RATIO = 16 / 9;
export const DESKTOP_DISPLAY_AD_BANNER_ASPECT_RATIO = 32 / 9;
const DISPLAY_AD_LOGO_ASPECT_RATIO = 1;
const DISPLAY_AD_BANNER_HEIGHT = 234;
const DISPLAY_AD_DESCRIPTION_CONTAINER_WIDTH = 260;

export const DisplayAdBanner: React.FC<Props> = ({
  displayAdContent,
  withinDetails = false,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const isSmallTabletOrSmaller = isSmallLayout || isSmallTabletLayout;
  const isLargerThanSmallTablet = !isSmallLayout && !isSmallTabletLayout;
  const adsDetailsWrapperLargeStyles =
    isLargerThanSmallTablet && !displayAdContent.logo_image_url
      ? [styles.adsDetailsWrapperLarge, styles.justifyContent]
      : styles.adsDetailsWrapperLarge;

  const {
    adsDetailsWrapper,
    adsImage,
    adsImageAndDescriptionStyle,
    adsImageWrapper,
    logoImageStyles,
    titleStyle,
  } = isSmallTabletOrSmaller
    ? {
        adsDetailsWrapper: styles.adsDetailsWrapper,
        adsImage: styles.adsImage,
        adsImageAndDescriptionStyle: styles.adsImageAndDescription,
        adsImageWrapper: styles.mobileAspectRatio,
        logoImageStyles: styles.logoImage,
        titleStyle: styles.title,
      }
    : {
        adsDetailsWrapper: adsDetailsWrapperLargeStyles,
        adsImage: [
          styles.adsImageLargeCommon,
          withinDetails ? styles.mobileAspectRatio : styles.adsImageLarge,
        ],
        adsImageAndDescriptionStyle: [
          styles.adsImageAndDescriptionLargeCommon,
          withinDetails
            ? styles.mobileAspectRatio
            : styles.adsImageAndDescriptionLarge,
        ],
        adsImageWrapper: styles.adsImageWrapperLarge,
        logoImageStyles: styles.logoImageLarge,
        titleStyle: styles.titleLarge,
      };

  const titleAndButton = (
    <>
      <Text style={[TYPOGRAPHY.pSemiBold, styles.titleBase, titleStyle]}>
        {displayAdContent.title}
      </Text>
      {isSmallTabletOrSmaller ? (
        <View style={styles.chevronWrapper}>
          <ChevronRight testID="chevron-right-icon" />
        </View>
      ) : (
        <View style={styles.learnMoreWrapper}>
          <Text style={styles.learnMoreText}>{i18n.t('learn_more')}</Text>
        </View>
      )}
    </>
  );

  return (
    <View style={adsImageAndDescriptionStyle}>
      {displayAdContent.title && !withinDetails ? (
        <View style={adsDetailsWrapper}>
          {displayAdContent.logo_image_url ? (
            <CachedImage
              alt="" // TODO: make this configurable by advertiser, currently don't have meaningful default alt text
              contentFit="contain"
              source={{ uri: displayAdContent.logo_image_url }}
              style={logoImageStyles}
              testID="logo-image"
            />
          ) : null}
          {isSmallTabletOrSmaller ? (
            titleAndButton
          ) : (
            <View style={styles.alignItemsStart}>{titleAndButton}</View>
          )}
        </View>
      ) : null}
      <View style={adsImageWrapper}>
        <CachedImage
          alt="" // TODO: make this configurable by advertiser, currently don't have meaningful default alt text
          contentFit="cover"
          source={{
            uri:
              isLargerThanSmallTablet &&
              displayAdContent.desktop_banner_image_url &&
              !withinDetails
                ? displayAdContent.desktop_banner_image_url
                : displayAdContent.banner_image_url,
          }}
          style={[adsImage, StyleSheet.absoluteFillObject]}
          testID="banner-image"
        />
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  adsDetailsWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingVertical: 8,
  },
  adsDetailsWrapperLarge: {
    alignItems: 'flex-start',
    backgroundColor: theme.colors.white,
    borderRadius: BORDER_RADIUS_SMALL,
    justifyContent: 'space-between',
    marginRight: -16,
    padding: SCREEN_PADDING,
    width: DISPLAY_AD_DESCRIPTION_CONTAINER_WIDTH,
    zIndex: 1,
  },
  adsImage: {
    aspectRatio: MOBILE_DISPLAY_AD_BANNER_ASPECT_RATIO,
    borderRadius: BORDER_RADIUS_SMALL,
  },
  adsImageAndDescription: {
    backgroundColor: theme.colors.white,
    borderRadius: BORDER_RADIUS_SMALL,
    marginLeft: LIST_MARGIN_HORIZONTAL,
    marginRight: LIST_MARGIN_HORIZONTAL,
    marginTop: LIST_MARGIN_HORIZONTAL,
  },
  adsImageAndDescriptionLarge: {
    height: DISPLAY_AD_BANNER_HEIGHT,
  },
  adsImageAndDescriptionLargeCommon: {
    borderRadius: BORDER_RADIUS_SMALL,
    flexDirection: 'row',
    marginBottom: 8,
    overflow: 'hidden',
    padding: SCREEN_PADDING_SMALL,
    paddingBottom: 0,
  },
  adsImageLarge: {
    aspectRatio: DESKTOP_DISPLAY_AD_BANNER_ASPECT_RATIO,
    height: DISPLAY_AD_BANNER_HEIGHT,
  },
  adsImageLargeCommon: {
    width: '100%',
  },
  adsImageWrapperLarge: {
    borderRadius: BORDER_RADIUS_SMALL,
    flex: 1,
    overflow: 'hidden',
  },
  alignItemsStart: {
    alignItems: 'flex-start',
  },
  chevronWrapper: {
    alignItems: 'center',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  justifyContent: {
    justifyContent: 'center',
  },
  learnMoreText: {
    ...FONTS.primarySemiBold,
    fontSize: theme.fontSizes.small,
    lineHeight: 24,
  },
  learnMoreWrapper: {
    borderColor: theme.colors.divider,
    borderRadius: BORDER_RADIUS_TINY,
    borderWidth: 1,
    paddingHorizontal: 8,
  },
  logoImage: {
    aspectRatio: DISPLAY_AD_LOGO_ASPECT_RATIO,
    height: 54,
  },
  logoImageLarge: {
    aspectRatio: DISPLAY_AD_LOGO_ASPECT_RATIO,
    height: 64,
  },
  mobileAspectRatio: {
    aspectRatio: MOBILE_DISPLAY_AD_BANNER_ASPECT_RATIO,
  },
  title: {
    flex: 1,
    fontSize: theme.fontSizes.medium,
  },
  titleBase: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    lineHeight: 24,
  },
  titleLarge: {
    fontSize: theme.fontSizes.large,
    marginBottom: 16,
  },
}));
